// 杂项工具方法

import type { InternalAxiosRequestConfig } from 'axios'
import qs from 'qs'
import { Tooltip as ATooltip } from '@arco-design/web-vue'
import type { RendererElement, RendererNode } from 'vue'
import { get, has, omit } from 'lodash-es'
import { divideNumbers, formatDecimal, multiplyNumbers } from './number-tools'
import { JsonParse } from './object-tools'
import { Currencies, DefaultCurrency } from '@/config/common'
import type { Amount } from '@/types/interfaces'
import { useTranslate } from '@/composables/use-translate'
import type { PageHeaderProps } from '@/components/page-header/types'
import { PeriodType } from '@/api/base/enums'

// const { translate } = useTranslate()

/**
 * 生成请求的唯一键
 * @param config Axios 请求配置
 * @returns 请求的唯一键
 */
export function generateRequestKey(config: InternalAxiosRequestConfig<any>): string {
  if (config && config.data) {
    config.data = JsonParse(config.data)
  }
  const { method, url, params, data } = config // 请求方式，参数，请求地址，
  return [method, url, qs.stringify(params), qs.stringify(data), '-'].filter(Boolean).join('&') // 拼接
}

// 生成随机字符串
export function randomString(length: number): string {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  if (/^\d/.test(result)) {
    return `a${result}`
  }
  return result
}

/**
 * 根据给定的百分比返回颜色代码。
 *
 * @param percentage - 百分比值。
 * @returns 颜色代码作为字符串。
 */
export function getPercentageColor(percentage: number): string {
  if (percentage === 0) {
    return '#979797'
  }
  else if (percentage >= 1 && percentage <= 35) {
    return '#E34F4F'
  }
  else if (percentage >= 36 && percentage <= 70) {
    return '#FFA800'
  }
  else if (percentage >= 71 && percentage <= 99) {
    return '#3D64E7'
  }
  else {
    return '#54C07C'
  }
}

/**
 * 金额展示
 *
 * @export
 * @param {(Amount | number)} amount 金额
 * @param {boolean} [raw] 是否输出原始值
 * @param {boolean} [abbr] 是否缩减展示
 * @return {*}  {string} 金额展示
 */
export function formatAmount(amount: Amount | number, raw = false, abbr = false, split = ' '): string {
  if (!amount) {
    amount = 0
  }
  const rawValue = typeof amount === 'number' || typeof amount === 'string' ? multiplyNumbers(amount, 1, false) : amount?.value || 0
  const value = raw ? rawValue : Math.max(rawValue, 0)
  const currency = typeof amount === 'number' ? DefaultCurrency : amount?.currency || DefaultCurrency
  const formattedAmount = abbr ? abbreviateNumber(value) : formatDecimal(value)
  const formattedAmountWithCommas = formattedAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const _currency = Currencies[currency] ?? '$'
  return `${_currency}${split}${formattedAmountWithCommas}`
}

function abbreviateNumber(number: number): string {
  if (number >= 1e9) {
    return `${formatDecimal(number / 1e9)}B`
  }
  if (number >= 1e6) {
    return `${formatDecimal(number / 1e6)}M`
  }
  if (number >= 1e3) {
    return `${formatDecimal(number / 1e3)}K`
  }
  return number.toString()
}

/**
 * 格式化文本展示，避免展示为空
 *
 * @export
 * @param {unknown} val 待格式化文本
 * @param {(string | number)} [defaultVal] 默认值
 * @return {*} 格式化后的结果
 */
export function nullVal(val: unknown, defaultVal: string | number = '-') {
  return (val || defaultVal) as string
}

/**
 * 表格列自定义显示方法
 *
 * @export
 * @param {unknown} string 显示的文本内容
 * @param {(string | number)} [defaultVal] 默认值
 * @return {*} Vnode 节点
 */
export function formatValue(string: unknown, defaultVal: string | number = '-') {
  return h('span', { innerHTML: nullVal(string, defaultVal) })
}

/**
 * 格式化自定义值
 * @param value 值
 * @param options 选项
 * @returns 格式化后的值
 */
export function formatCustomValue(value: unknown, options: Record<string, any> = { defaultVal: '-', class: '' }) {
  const { defaultVal, class: className } = options
  return h(
    'span',
    { innerHTML: nullVal(value, defaultVal), class: className },
  )
}

/**
 * 语言枚举显示，主要是用在表格中
 *
 * @export
 * @param {({ group?: string, path: string | string[], props?: Record<string, any> })} options 配置参数
 * @return {*}  {string} 多语言枚举
 */
export function formatEnumValue(options: { group?: string, path: string | string[], props?: Record<string, any> }): string {
  const { group = 'enums', path = [], props = {} } = options || {}
  let paths = ''
  if (Array.isArray(path)) {
    paths = [group, ...path].join('.')
  }
  else {
    paths = `${group}.${path}`
  }
  const { translate } = useTranslate()
  return translate(paths, props)
}

/**
 * 格式化数值展示，避免显示负数，最小值显示 0
 *
 * @export
 * @param {number} number 待格式化数值
 * @param {number} [precision] 保留小数位
 * @return {*} 格式化后的数值，最小值 0
 */
export function formatNumber(number: number, precision = 0) {
  return Math.max(multiplyNumbers(number, 1, precision > 0, precision), 0)
}

/**
 * 计算百分比，最大值100
 * @param dividend 除数
 * @param divisor 被除数
 * @returns 百分比结果
 */
export function calculatePercentage(dividend: number, divisor: number): number {
  if (divisor * 1 === 0) {
    return 0
  }
  const percentage = multiplyNumbers(
    divideNumbers(dividend * 1, divisor * 1, false),
    100,
  )
  // return Math.min(percentage, 100)
  return percentage
}

/**
 * 白底黑字的 tooltip 弹窗方法
 * @param attrs tooltip props
 * @param content tooltip slot
 * @returns tooltip
 */
export function whiteBgTooltip(attrs: typeof ATooltip['props'], content: globalThis.VNode<RendererNode, RendererElement, {
  [key: string]: any
}>) {
  return h(ATooltip, { backgroundColor: '#FFFFFF', contentStyle: { color: '#696969' }, ...attrs }, content)
}

export function formatRequestParams(params: PageHeaderProps) {
  const { periodType } = params
  if (periodType !== PeriodType.Range) {
    return omit(params, ['startTime', 'endTime'])
  }
  return params
}

// 校验值是否存在，存在返回V2的值，否则返回原值
export function checkV2Value<T = any>(key: string, data: Record<string, T>): T {
  const v2Key = `${key}V2`
  return data[v2Key] ?? data[key]
}
